// src/utils/buildQuillModules.ts

import type {
  QuillBackgroundOption,
  QuillColorOption,
  QuillFontOption,
  QuillModules,
  QuillSizeOption,
  QuillSizeValue,
  QuillToolbarConfig,
  QuillToolbarGroup,
  ToolbarGroupName,
} from '@/config/quillTypes';
import { DEFAULT_FONTS } from '@/config/quillSetup';

/**
 * Default toolbar order if not specified in config
 */
const DEFAULT_TOOLBAR_ORDER: ToolbarGroupName[] = [
  'formatting',
  'header',
  'font-size',
  'list',
  'color',
  'align',
  'media',
  'blocks',
  'clean',
];

/**
 * Convert toolbar config to Quill modules format
 * This is a pure function that transforms configuration into Quill-compatible format
 */
export const buildQuillModules = (config: QuillToolbarConfig): QuillModules => {
  // Build all toolbar groups into a map
  const toolbarGroups: Partial<Record<ToolbarGroupName, QuillToolbarGroup>> = {};

  // Build formatting group (bold, italic, underline, strike)
  const formats: string[] = [];
  if (config.bold) formats.push('bold');
  if (config.italic) formats.push('italic');
  if (config.underline) formats.push('underline');
  if (config.strike) formats.push('strike');
  if (formats.length > 0) {
    toolbarGroups.formatting = formats;
  }

  // Build header group
  if (config.header && config.header.length > 0) {
    toolbarGroups.header = [{ header: config.header }];
  }

  // Build font-size group
  const fontSizeGroup: Array<QuillFontOption | QuillSizeOption> = [];
  if (config.font) {
    const fontValue: string[] = config.font === true ? DEFAULT_FONTS : config.font;
    fontSizeGroup.push({ font: fontValue });
  }
  if (config.size) {
    const sizeValue: QuillSizeValue[] =
      config.size === true ? ['12px', '14px', '16px', '18px', '20px', '24px', '32px'] : config.size;
    fontSizeGroup.push({ size: sizeValue });
  }
  if (fontSizeGroup.length > 0) {
    toolbarGroups['font-size'] = fontSizeGroup;
  }

  // Build list group
  if (config.list && config.list.length > 0) {
    const listGroup: Array<{ list: 'ordered' | 'bullet' }> = [];
    if (config.list.includes('ordered')) listGroup.push({ list: 'ordered' });
    if (config.list.includes('bullet')) listGroup.push({ list: 'bullet' });
    if (listGroup.length > 0) {
      toolbarGroups.list = listGroup;
    }
  }

  // Build color group
  const colors: Array<QuillColorOption | QuillBackgroundOption> = [];
  if (config.color) {
    const colorValue: string[] = config.color === true ? [] : config.color;
    colors.push({ color: colorValue });
  }
  if (config.background) {
    const bgValue: string[] = config.background === true ? [] : config.background;
    colors.push({ background: bgValue });
  }
  if (colors.length > 0) {
    toolbarGroups.color = colors;
  }

  // Build align group
  if (config.align && config.align.length > 0) {
    toolbarGroups.align = [{ align: config.align }];
  }

  // Build media group (link, image, video)
  const media: string[] = [];
  if (config.link) media.push('link');
  if (config.image) media.push('image');
  if (config.video) media.push('video');
  if (media.length > 0) {
    toolbarGroups.media = media;
  }

  // Build blocks group (blockquote, code-block)
  const blocks: string[] = [];
  if (config.blockquote) blocks.push('blockquote');
  if (config['code-block']) blocks.push('code-block');
  if (blocks.length > 0) {
    toolbarGroups.blocks = blocks;
  }

  // Build clean group
  if (config.clean) {
    toolbarGroups.clean = ['clean'];
  }

  // Assemble toolbar in the specified order
  const order = config.toolbarOrder || DEFAULT_TOOLBAR_ORDER;
  const toolbar: QuillToolbarGroup[] = [];

  for (const groupName of order) {
    const group = toolbarGroups[groupName];
    if (group) {
      toolbar.push(group);
    }
  }

  return { toolbar };
};
